import { useState } from 'react';
import { useLang } from '../i18n/lang';
import { LANGS } from '../i18n/strings';
import { useTranslated } from './useTranslated';
import type { TextTranslator } from '../i18n/translate';

/*
 * A ROUGH AUTOMATIC READING OF ONE VERSE, on request only.
 *
 * Scripture is not safe to machine-translate silently: an on-device model turns "give full measure
 * and full weight" into "combine the measure and run wild", and for a sacred text that is not a
 * nuance but a falsehood shown as a rendering. So nothing is translated by default. The reader is
 * offered a control; only when they open it does the translation run, and it arrives plainly marked
 * as an automatic reading, NOT an authoritative translation — on its own line, so it never mixes into
 * the verse. Where the reader's language already has an authoritative text (the passage's own
 * original), the caller does not render this at all.
 *
 * AND ON HADITH, WHERE IT IS THE ONLY READING THERE IS.
 *
 * The paragraph above assumes an authoritative English sits beside the machine one, and for the
 * reports that assumption fails: no openly-licensed complete English of Bukhari or Muslim exists and
 * none can be licensed, so a reader who does not read Arabic has nothing. `from` is 'ar' there rather
 * than 'en', and the source is the report as printed.
 *
 * That cuts both ways and the balance is unchanged. Being the only reading makes it far more valuable
 * and makes a bad rendering far more damaging — there is no correct text next to it to catch the
 * error. So the rules stay exactly as they are: nothing runs unasked, and every result carries the
 * caveat. What must NOT happen is a second, looser translation path growing for the reports because
 * this one felt too cautious for them.
 */
export interface VerseTranslationProps {
  /** The English (public-domain) text to translate — the only text a machine reading derives from. */
  text: string;
  /** The source language of `text`. */
  from?: string;
  /** The translator to use; omitted, the browser's on-device one (a test passes a fake or null). */
  translator?: TextTranslator | null;
}

export function VerseTranslation({ text, from = 'en', translator }: VerseTranslationProps): React.JSX.Element | null {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);

  if (lang === from) return null; // the reader already reads the source language
  const label = LANGS[lang].label;

  return (
    <div className="verse-mt">
      <button
        type="button"
        className="verse-mt-toggle"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        {open ? t('translate.hide') : t('translate.to', { lang: label })}
      </button>
      {open && <AutomaticReading text={text} from={from} to={lang} label={label} translator={translator} />}
    </div>
  );
}

/** The reading itself — mounted only once the reader asks, so the translation runs on request. */
function AutomaticReading(
  { text, from, to, label, translator }:
  { text: string; from: string; to: string; label: string; translator?: TextTranslator | null },
): React.JSX.Element | null {
  const { dir, t } = useLang();
  const result = useTranslated(text, from, to, translator);

  if (result.status === 'translating') {
    return <p className="verse-mt-body is-loading" lang={to} dir={dir}>{t('translate.inProgress')}</p>;
  }
  if (result.status === 'done' && result.text) {
    return (
      <>
        <p className="verse-mt-body" lang={to} dir={dir}>{result.text}</p>
        <p className="verse-mt-caveat">{t('translate.caveat')}</p>
      </>
    );
  }
  if (result.status === 'unsupported') {
    return <p className="verse-mt-caveat">{t('translate.unsupported', { lang: label })}</p>;
  }
  if (result.status === 'error') {
    return <p className="verse-mt-caveat">{t('translate.error')}</p>;
  }
  return null;
}
